import React from 'react';
import { isExtensionContext } from '@common/services/detect-context';
import { ContentMessageServiceActions } from '@extension/context/content/services/contentMessageServiceActions';

/**
 * How many resolved assets are kept in memory at once.
 *
 * The extension path turns every asset into a `data:` URL, and a base64 payload
 * of a full-size image is not small. A calendar month can put a couple of
 * hundred image-bearing chips on screen, so an unbounded cache would grow to
 * tens of megabytes and never give any of it back. 64 entries covers "the month
 * I am looking at" with room to spare; anything older is dropped least-recently
 * -used and simply re-fetched if the user navigates back to it.
 *
 * Note what is NOT needed here: revocation. `URL.createObjectURL` blob URLs must
 * be revoked or they pin their blob for the life of the document — and evicting
 * one that an <img> is still painting would blank that image. A `data:` URL has
 * no such handle: dropping it from the cache frees nothing that is still in use,
 * because any <img> still showing it holds the string itself. That is the reason
 * this path stays on data URLs rather than "upgrading" to blob URLs.
 */
const MAX_CACHED_ASSETS = 64;

/**
 * Resolves asset URLs for both website and extension contexts
 * In website context: returns the original URL for direct usage
 * In extension context: fetches via background script and returns data URL
 *
 * Why the extension needs this at all: the overlay lives inside web.whatsapp.com,
 * whose Content-Security-Policy allows `img-src 'self' data: blob:` plus
 * WhatsApp's own hosts — and NOT our asset bucket. An <img> pointed straight at
 * `blueticks-assets.storage.googleapis.com` is refused by the page before it
 * ever reaches the network, and a page-context `fetch()` of it throws for the
 * same reason. The background service worker is not a page and is not bound by
 * a page's CSP, so it can fetch the bytes and hand back a `data:` URL — which
 * that same CSP explicitly allows.
 */
export class AssetUrlResolver {
  /** In-flight (and recently completed) work, keyed by source URL. */
  private static cache = new Map<string, Promise<string>>();
  /** Settled values only — the half that can be read synchronously. */
  private static resolved = new Map<string, string>();

  /**
   * Get a usable URL for the given asset URL
   * @param url - The original asset URL
   * @returns Promise<string> - A URL that can be used in the current context
   */
  static async resolveAssetUrl(url: string): Promise<string> {
    if (!url) return '';

    // For website context, return URL directly. No CSP problem there, and no
    // reason to send a perfectly loadable URL through a worker that, on the
    // website, does not even exist.
    if (!isExtensionContext()) {
      return url;
    }

    // For extension context, check cache first
    const inFlight = this.cache.get(url);
    if (inFlight) {
      this.touch(url);
      return inFlight;
    }

    // Create and cache the promise
    const promise = this.fetchAssetForExtension(url);
    this.cache.set(url, promise);

    return promise;
  }

  /**
   * The already-known answer for `url`, or undefined if there isn't one yet.
   *
   * Lets a component paint a cached asset on its FIRST render instead of
   * mounting empty and filling in an effect a tick later. That matters for the
   * calendar: a chip whose picture arrives late is a chip that renders its text
   * layout and then re-renders as a thumbnail — a visible flip on every month
   * change, for an asset we already hold.
   */
  static peekResolved(url: string): string | undefined {
    if (!url) return undefined;
    if (!isExtensionContext()) return url;
    // A chip that remounts with its asset already resolved reads it HERE, never
    // through resolveAssetUrl — so a peek is the use the LRU has to see, or the
    // picture on screen is the first thing evicted.
    this.touch(url);
    return this.resolved.get(url);
  }

  /**
   * Fetch asset via background script for extension context
   */
  private static async fetchAssetForExtension(url: string): Promise<string> {
    try {
      const result = await ContentMessageServiceActions.fetchUrlContentAsBlob(url);
      if (!result) {
        throw new Error('No data received from background script');
      }
      this.remember(url, result);
      return result;
    } catch (error) {
      console.error('Error fetching asset in extension context:', error);
      // Forget the failure rather than caching it. A worker fetch can fail for
      // reasons that do not repeat (worker asleep, message timeout, a blip on
      // the bucket); caching the empty string would make one bad moment
      // permanent for the life of the page. The caller still gets '' now — and
      // every caller treats '' as "no picture", which degrades to text — but a
      // later render is allowed to try again.
      this.cache.delete(url);
      return '';
    }
  }

  /** Record a settled value, evicting the least recently used once over cap. */
  private static remember(url: string, value: string): void {
    this.resolved.set(url, value);
    while (this.resolved.size > MAX_CACHED_ASSETS) {
      const oldest = this.resolved.keys().next();
      if (oldest.done) break;
      this.resolved.delete(oldest.value);
      this.cache.delete(oldest.value);
    }
  }

  /** Move a key to the young end of the Map's insertion order (LRU bookkeeping). */
  private static touch(url: string): void {
    if (!this.resolved.has(url)) return;
    const value = this.resolved.get(url)!;
    this.resolved.delete(url);
    this.resolved.set(url, value);
  }

  /**
   * Clear the cache (useful for cleanup or testing)
   */
  static clearCache(): void {
    this.cache.clear();
    this.resolved.clear();
  }

  /**
   * Remove specific URL from cache
   */
  static invalidateUrl(url: string): void {
    this.cache.delete(url);
    this.resolved.delete(url);
  }
}

/**
 * Simple function to get a resolved asset URL (non-React)
 * @param url - The original asset URL
 * @returns Promise<string> - A URL that can be used in the current context
 */
export const getAssetUrl = (url: string): Promise<string> => {
  return AssetUrlResolver.resolveAssetUrl(url);
};

/**
 * React hook for resolving asset URLs
 * @param url - The original asset URL
 * @returns { resolvedUrl: string, isLoading: boolean, error: string | null }
 */
export const useAssetUrl = (url: string) => {
  // Seeded from the cache so an asset we already hold is on screen in the very
  // first paint; only a genuinely unknown asset starts in the loading state.
  const seed = url ? AssetUrlResolver.peekResolved(url) : undefined;
  const [resolvedUrl, setResolvedUrl] = React.useState<string>(seed ?? '');
  const [isLoading, setIsLoading] = React.useState<boolean>(!!url && seed === undefined);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!url) {
      setResolvedUrl('');
      setIsLoading(false);
      setError(null);
      return;
    }

    const known = AssetUrlResolver.peekResolved(url);
    if (known !== undefined) {
      setResolvedUrl(known);
      setIsLoading(false);
      setError(null);
      return;
    }

    // A month change can unmount a chip while its asset is still in the air.
    // Without this guard the late resolve would setState on a dead component.
    let live = true;
    setIsLoading(true);
    setError(null);

    AssetUrlResolver.resolveAssetUrl(url)
      .then((resolved) => {
        if (!live) return;
        setResolvedUrl(resolved);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        console.error('Error resolving asset URL:', err);
        setError(err.message || 'Failed to load asset');
        setResolvedUrl('');
      })
      .finally(() => {
        if (!live) return;
        setIsLoading(false);
      });

    return () => { live = false; };
  }, [url]);

  return { resolvedUrl, isLoading, error };
};
